import {
  Component,
  computed,
  ElementRef,
  OnDestroy,
  OnInit,
  Signal,
  signal,
  ViewChild,
  WritableSignal,
  inject,
} from '@angular/core';
import { FieldPathDirective, FormNonFieldErrorsComponent, FormOptionsDirective } from '@bo/ng-forms';
import {
  Employee,
  EmployeeRequest,
  EmployeeRoleType,
  IdNumType,
  TopUpFrequency,
} from '../../../../../_interfaces/employee.interface';
import { topUpSelectOptions } from '../../../card-topups/utils/utils';
import { EmployeeApiService } from '../../../../../_services/employee-service/api/employee-api.service';
import { OffcanvasService } from '../../../../../_services/offcanvas-service/offcanvas.service';
import { ActivatedRoute, Params } from '@angular/router';
import { ToastService } from '../../../../../_services/toast-service/toast.service';
import { Store } from '@ngxs/store';
import { AppSelectors } from '../../../../../store/app/app.selectors';
import { Observable, of } from 'rxjs';
import { Method } from '@bo/ng-api';
import { HttpErrorResponse, HttpParams } from '@angular/common/http';
import { AvatarComponent } from '../../../../../shared/components/avatar/avatar.component';
import { NgClass } from '@angular/common';
import { FieldComponent } from '../../../../../shared/components/forms/form-field/form-field.component';
import { FieldUnverifiedTextComponent } from '../../../../../shared/components/forms/field-unverified-text/field-unverified-text.component';
import { IDFieldComponent } from '../../../../../shared/components/forms/id-field/id-field.component';
import { LoaderComponent } from '../../../../../shared/components/loader/loader.component';
import { NgbModal, NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { OffcanvasCancelBtnComponent } from '../../../../../shared/components/offcanvas/offcanvas-cancel-btn/offcanvas-cancel-btn.component';
import { PhoneFieldComponent } from '../../../../../shared/components/forms/phone-field/phone-field.component';
import { ReactiveFormsModule } from '@angular/forms';
import { SelectFieldComponent } from '../../../../../shared/components/forms/select-field/select-field.component';
import { SubmitComponent } from '../../../../../shared/components/forms/submit/submit.component';
import { maskConfig } from '../../../../../shared/configs/configs';
import { EmployeeService } from '../../../../../_services/employee-service/employee.service';
import { SelectTopupFrequencyComponent } from '../../../../../shared/components/forms/select-topup-frequency/select-topup-frequency.component';
import { CardLimitDisplayComponent } from '../../../../../shared/components/cards/card-limit-display/card-limit-display.component';
import { ToggleSwitchComponent } from '../../../../../shared/components/forms/toggle-switch/toggle-switch.component';
import { approverTooltipText, topupTooltipText } from '../../../../../_fixtures/project-fixtures';
import { ContactInfoEditedNoticeModalComponent } from '../../active-employees/contact-info-edited-notice-modal/contact-info-edited-notice-modal.component';
import { toSignal } from '@angular/core/rxjs-interop';
import { DashboardService } from '../../../../../_services/dashboard-api-service/dashboard.service';
import { SelectOption } from '../../../../../_interfaces/select-field.interface';
import { AppFormComponent } from '../../../../../shared/classes/app-form';

@Component({
  selector: 'app-employee-form-base',
  imports: [
    AvatarComponent,
    FieldComponent,
    FieldPathDirective,
    FieldUnverifiedTextComponent,
    FormNonFieldErrorsComponent,
    IDFieldComponent,
    LoaderComponent,
    NgbTooltip,
    OffcanvasCancelBtnComponent,
    PhoneFieldComponent,
    ReactiveFormsModule,
    SelectFieldComponent,
    SubmitComponent,
    NgClass,
    FormOptionsDirective,
    SelectTopupFrequencyComponent,
    CardLimitDisplayComponent,
    ToggleSwitchComponent,
  ],
  templateUrl: './employee-form-base.component.html',
})
export class EmployeeFormBaseComponent extends AppFormComponent<EmployeeRequest> implements OnInit, OnDestroy {
  readonly store = inject(Store);
  protected employeeApiService = inject(EmployeeApiService);
  protected employeeService = inject(EmployeeService);
  readonly dashboardService = inject(DashboardService);
  protected offcanvasService = inject(OffcanvasService);
  readonly route = inject(ActivatedRoute);
  protected toastService = inject(ToastService);
  readonly modalService = inject(NgbModal);
  readonly userProfile$ = this.store.select(AppSelectors.userDetails);

  @ViewChild('scrollContainer') private readonly scrollContainer!: ElementRef;
  @ViewChild('fadeContainer') protected readonly fadeContainer!: ElementRef;

  approverOptions$ = this.employeeService.employeeAllOptions();
  roleOptions$: Observable<SelectOption[]> = of([
    { option: 'Cardholder', value: EmployeeRoleType.USER },
    { option: 'Admin', value: EmployeeRoleType.ADMIN },
  ]);

  protected readonly TopUpFrequency = TopUpFrequency;
  protected readonly IdNumType = IdNumType;
  protected readonly fields: string[] = [
    'first_name',
    'last_name',
    'idnum_type',
    'idnum',
    'phone',
    'email',
    'role',
    'create_card',
    'topup_amount',
    'approvers',
    'date_of_birth',
  ];
  protected readonly selectOptions = topUpSelectOptions;
  protected readonly maskConfig = maskConfig;
  ruleFrequency: TopUpFrequency | null | undefined;
  employeeData = signal<Employee>({} as Employee);
  invitationData = signal<EmployeeRequest>({} as EmployeeRequest);
  isEditingPhone: boolean = false;
  isEditingEmail: boolean = false;
  payload: EmployeeRequest | undefined;
  protected returnUrl: Signal<string> = computed(() => {
    return this.employeeId() ? `/employees/view/${this.employeeId()}` : '/employees';
  });

  returnParams = new HttpParams();
  availableCards = toSignal(this.dashboardService.getAvailableCardCount(), { initialValue: null });
  hasAvailableCards = computed(() => (this.availableCards() ?? 0) > 0);
  showNoCardsAvailable = computed(() => {
    if (this.existingEmployee()) {
      return !this.hasAvailableCards() && !this.employeeData().is_card_holder;
    } else {
      return !this.hasAvailableCards() && !this.invitationData().create_card;
    }
  });

  employeeId = signal<string>('');
  protected showRecurringTopUp = signal<boolean>(false);
  protected showApproverFields = signal<boolean>(false);
  protected hasCardLimitError = signal<boolean>(false);
  protected hasCardMinWarning = signal<boolean>(false);
  protected showRecurringSwitch = signal<boolean>(true);
  protected existingEmployee = computed(() => {
    return !!this.employeeId();
  });
  protected selectedFrequency: WritableSignal<TopUpFrequency> = signal<TopUpFrequency>(TopUpFrequency.Monthly);

  protected isDisabled = computed(() => {
    return this.isFormInvalid() || this.hasCardLimitError() || this.hasCardMinWarning();
  });

  protected isFormInvalid = signal<boolean>(true);
  readonly topupTooltipText = topupTooltipText;
  readonly approverTooltipText = approverTooltipText;

  constructor() {
    super();
    this.route.params.subscribe({
      next: (params: Params) => {
        this.employeeId.set(params['id']);
      },
    });
  }

  override ngOnInit() {
    super.ngOnInit();
  }

  override createForm(): void {
    const options$ = this.employeeApiService.getOptions(this.employeeApiService.URL_EMPLOYEE_SIGNUP + '/', Method.POST);
    this.subscriptions.add(this.buildFromOptions(options$).subscribe());
  }

  protected handleRoleValueChanges(): void {
    this.subscriptions.add(
      this.form.get('role')?.valueChanges.subscribe((value) => {
        if (value === 'User') {
          this.forceCreateCard();
        } else if (value === 'Admin') {
          this.toggleCreateCard();
        } else {
          this.showApproverFields.set(false);
        }
      }),
    );
  }

  handleCardLimitError($event: boolean) {
    this.hasCardLimitError.set($event);
  }

  protected toggleCreateCard(): void {
    return;
  }

  protected handleCreateCardToggleChanges(): void {
    this.form.get('create_card')?.valueChanges.subscribe((value) => {
      this.showApproverFields.set(value);
      // if card not selected, make sure recurring topup is false otherwise form is disabled.
      if (!value) this.showRecurringTopUp.set(false);
      this.setCardFieldsBasedOnCreateCard();
      this.setRecurringFields();
      if (value) {
        setTimeout(() => {
          this.scrollToBottom();
        }, 0);
      }
    });
  }

  protected setCardFieldsBasedOnCreateCard() {
    if (this.showApproverFields()) {
      //if new invitation enable recurring topup
      this.form.get('approvers')?.enable();
    } else {
      this.form.get('approvers')?.disable();
    }
    if (this.showRecurringTopUp()) {
      this.form.get('topup_amount')?.enable();
    } else {
      this.form.get('topup_amount')?.disable();
    }
  }

  protected forceCreateCard() {
    this.form.get('create_card')?.setValue(true);
    this.form.get('create_card')?.disable();
    this.showApproverFields.set(true);
  }

  protected handleTopUpAmountChanges(): void {
    this.form.get('topup_amount')?.valueChanges.subscribe((amount) => {
      if (amount === 0) this.form.get('topup_amount')?.setValue(undefined, { emitEvent: false });
    });
  }

  onToggleRecurring(event: boolean) {
    this.showRecurringTopUp.set(event);
    this.setRecurringFields();
    setTimeout(() => {
      this.scrollToBottom();
    }, 0);
  }

  setRecurringFields() {
    if (this.showRecurringTopUp()) {
      this.form.get('topup_amount')?.enable();
    } else {
      this.form.get('topup_amount')?.disable();
      this.form.get('topup_amount')?.setValue(null);
    }
  }

  scrollToBottom(): void {
    // Find the LAST child element within the scroll container
    const container = this.scrollContainer.nativeElement as HTMLElement;
    const lastElement = container.lastElementChild;

    if (lastElement) {
      // Use scrollIntoView on the last element with smooth behavior
      lastElement.scrollIntoView({
        behavior: 'smooth',
        block: 'end', // Scroll to align the element with the end of the viewport
      });
    } else {
      // Fallback for empty container: scroll container to bottom (if necessary)
      container.scrollTop = container.scrollHeight;
    }
  }

  handleFrequencyChange(value: TopUpFrequency) {
    this.selectedFrequency.set(value);
  }

  // need this here as a placeholder.
  override submitData(formData: EmployeeRequest): Observable<EmployeeRequest> {
    return of(formData);
  }

  handleSubmitSuccess(): void {
    this.offcanvasService.close();
    if (this.employeeId()) {
      this.toastService.show('Employee information updated successfully.', 'iconoir-check-circle');
      this.offcanvasService.offCanvasNavigate(`/employees`, this.returnParams);
    } else {
      const admin = this.form.get('role')?.value === EmployeeRoleType.ADMIN;
      this.toastService.show(
        `You have successfully added ${admin ? 'an admin user.' : 'a cardholder.'}`,
        'iconoir-check-circle',
      );
      this.offcanvasService.offCanvasNavigate(`/employee-invitations`);
    }
    if (this.isEditingEmail || this.isEditingPhone) {
      this.showEditedContactInfoModal(this.payload, this.employeeData()?.email);
    }
  }

  showEditedContactInfoModal(payload?: EmployeeRequest, existingEmail?: string) {
    const modalRef = this.modalService.open(ContactInfoEditedNoticeModalComponent, {
      centered: true,
      size: 'md',
    });
    modalRef.componentInstance.newEmail = payload?.email;
    modalRef.componentInstance.existingEmail = existingEmail;
    modalRef.componentInstance.phone = payload?.phone;
  }

  protected override getGenericFormErrorMessage(): string {
    return 'We could not save this profile. Please review the errors below.';
  }

  protected override handleSubmitError(error: unknown) {
    // If the error is related to the card limit being reached
    if (error instanceof HttpErrorResponse && error.error.card) {
      const message = error.error.card[0];
      this.form.setErrors({ server: message });
      return;
    }
    // Else use the default error handling
    super.handleSubmitError(error);

    // Handle ID number already exists error, AFTER super
    if (error instanceof HttpErrorResponse && error.error.idnum?.[0] === 'Already exists.') {
      const isPassport = this.form.get('idnum_type')?.value === IdNumType.Passport;
      this.form.get('idnum')?.setErrors({
        server: [`An employee with this ${isPassport ? 'passport' : 'ID'} number already exists.`],
      });
    }
  }

  onRemoveInvitation(row: EmployeeRequest) {
    this.employeeService.removeInvitationPrompt(row, this.employeeId());
  }

  override ngOnDestroy() {
    super.ngOnDestroy();
    this.subscriptions.unsubscribe();
  }
}
